
import * as React from "react";
import {
  format,
  subYears,
  getYear,
  getMonth,
  getDate,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: (date: Date) => boolean;
  className?: string;
}

export function DatePicker({
  label,
  value,
  onChange,
  placeholder = "Select date",
  disabled,
  className,
}: DatePickerProps) {
  const today = new Date();
  const maxDate = subYears(today, -10); // Allow future dates up to 10 years
  const minDate = subYears(today, 120); // Reasonable minimum date

  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const [step, setStep] = React.useState<"year" | "month" | "day">("year");

  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = React.useState<number | null>(null);

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setStep("month");
  };

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    setStep("day");
  };

  const handleDateSelect = (date: Date) => {
    onChange(date);
    setIsCalendarOpen(false);
    setStep("year");
    setSelectedYear(null);
    setSelectedMonth(null);
  };

  const renderYearGrid = () => {
    const startYear = maxDate.getFullYear();
    const endYear = minDate.getFullYear();

    const years = [];
    for (let y = startYear; y >= endYear; y--) {
      years.push(y);
    }

    return (
      <div className="grid grid-cols-4 gap-2 p-3 max-h-[300px] overflow-y-auto">
        {years.map((year) => (
          <Button
            key={year}
            variant="ghost"
            className="w-full"
            onClick={() => handleYearSelect(year)}
          >
            {year}
          </Button>
        ))}
      </div>
    );
  };

  const renderMonthGrid = () => {
    const months = Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString("default", { month: "short" })
    );

    return (
      <div className="grid grid-cols-4 gap-2 p-3">
        {months.map((month, index) => (
          <Button
            key={month}
            variant="ghost"
            className="w-full"
            onClick={() => handleMonthSelect(index)}
          >
            {month}
          </Button>
        ))}
      </div>
    );
  };

  const renderDayGrid = () => {
    if (selectedYear === null || selectedMonth === null) return null;

    const start = startOfMonth(new Date(selectedYear, selectedMonth));
    const end = endOfMonth(start);
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="grid grid-cols-7 gap-2 p-3">
        {days.map((day) => {
          const isDisabled = disabled ? disabled(day) : (day > maxDate || day < minDate);
          return (
            <Button
              key={day.toISOString()}
              variant="ghost"
              className={cn(
                "w-full",
                isDisabled && "opacity-30 pointer-events-none"
              )}
              onClick={() => handleDateSelect(day)}
            >
              {getDate(day)}
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={cn("grid gap-2", className)}>
      {label && <Label>{label}</Label>}
      <Popover
        open={isCalendarOpen}
        onOpenChange={(open) => {
          setIsCalendarOpen(open);
          if (!open) {
            setStep("year");
            setSelectedYear(null);
            setSelectedMonth(null);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "justify-start text-left font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "PPP") : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {step === "year" && renderYearGrid()}
          {step === "month" && renderMonthGrid()}
          {step === "day" && renderDayGrid()}
        </PopoverContent>
      </Popover>
    </div>
  );
}
